import React, { useState } from 'react';
import './PrivacyPolicyPage.scss';

interface AccordionItem {
    id: string;
    title: string;
    content: string;
}

const section4Items: AccordionItem[] = [
    {
        id: '4.1',
        title: '4.1. Право на доступ к персональным данным',
        content: 'Субъект персональных данных имеет право получать информацию о том, обрабатываются ли его персональные данные оператором. При этом оператор обязан предоставить субъекту информацию о целях обработки, категориях обрабатываемых данных, сроках обработки, а также о лицах, которым передаются персональные данные.'
    },
    {
        id: '4.2',
        title: '4.2. Право на уточнение и изменение данных',
        content: 'Субъект персональных данных вправе требовать уточнения, блокирования или уничтожения своих персональных данных в случае, если они являются неполными, устаревшими, неточными, незаконно полученными или не являются необходимыми для заявленной цели обработки.'
    },
    {
        id: '4.3',
        title: '4.3. Право на отзыв согласия',
        content: 'В случаях, когда обработка персональных данных осуществляется на основании согласия субъекта, последний имеет право отозвать такое согласие. Отзыв согласия не влияет на законность обработки, осуществлённой до его отзыва.'
    },
    {
        id: '4.4',
        title: '4.4. Право на обжалование',
        content: 'Субъект персональных данных имеет право обжаловать действия или бездействие оператора в Национальный центр защиты персональных данных Республики Беларусь или в судебном порядке в соответствии с законодательством Республики Беларусь.'
    }
];

export function PrivacyPolicyPage() {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const toggleSection = (id: string) => {
        setExpandedId(expandedId === id ? null : id);
    };

    return (
        <div className='privacy-policy'>
            <h1 className='privacy-policy__title'>Политика конфиденциальности</h1>
            <p className='privacy-policy__updated'>Дата последнего обновления: март 2025 г.</p>

            <section className='privacy-policy__section'>
                <h2>1. Общие положения</h2>
                <p>
                    Настоящая Политика конфиденциальности (далее — Политика) определяет порядок обработки персональных данных
                    и меры по обеспечению их безопасности в Обществе с ограниченной ответственностью «Вояж Глобал» (далее — Оператор),
                    УНП 193765582, расположенном по адресу: Республика Беларусь, г. Минск, ул. В. Хоружей, 3, офис 305а.
                </p>
                <p>
                    Политика разработана в соответствии с Законом Республики Беларусь от 7 мая 2021 г. № 99-З «О защите персональных данных»
                    и распространяется на все бизнес-процессы Оператора, связанные с обработкой персональных данных.
                </p>
            </section>

            <section className='privacy-policy__section'>
                <h2>2. Порядок и условия обработки персональных данных</h2>
                <p>
                    Оператор осуществляет сбор, систематизацию, накопление, хранение, уточнение, извлечение, использование,
                    передачу, обезличивание, блокирование и уничтожение персональных данных в целях оказания туристических услуг,
                    информирования о турах и специальных предложениях, обработки заявок и обратной связи.
                </p>
                <p>
                    Источниками получения персональных данных являются: субъект персональных данных при заполнении форм на сайте,
                    при обращении по телефону или электронной почте, а также при заключении договоров.
                </p>
            </section>

            <section className='privacy-policy__section'>
                <h2>3. Уполномоченные лица и трансграничная передача</h2>
                <p>
                    Обработка персональных данных может осуществляться уполномоченными лицами Оператора, а также третьими лицами
                    (туроператоры, авиакомпании, отели) исключительно в объёме, необходимом для исполнения договоров и оказания услуг.
                </p>
                <p>
                    Трансграничная передача персональных данных осуществляется в случаях, когда это необходимо для бронирования
                    туров и оказания услуг, с соблюдением требований законодательства Республики Беларусь.
                </p>
            </section>

            <section className='privacy-policy__section'>
                <h2>4. Права субъектов персональных данных</h2>
                <p className='privacy-policy__intro'>
                    Субъект персональных данных обладает рядом прав в отношении своих данных. Ниже представлены основные права и порядок их реализации:
                </p>
                <div className='privacy-policy__accordion'>
                    {section4Items.map((item) => (
                        <div key={item.id} className='privacy-policy__accordion-item'>
                            <button
                                type='button'
                                className={`privacy-policy__accordion-header ${expandedId === item.id ? 'expanded' : ''}`}
                                onClick={() => toggleSection(item.id)}
                                aria-expanded={expandedId === item.id}
                            >
                                <span>{item.title}</span>
                                <span className='privacy-policy__accordion-icon' aria-hidden>▼</span>
                            </button>
                            {expandedId === item.id && (
                                <div className='privacy-policy__accordion-content'>
                                    <p>{item.content}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            <section className='privacy-policy__section'>
                <h2>5. Меры по обеспечению безопасности</h2>
                <p>
                    Оператор принимает необходимые организационные и технические меры для защиты персональных данных от неправомерного
                    доступа, уничтожения, изменения, блокирования, копирования, распространения, а также от иных неправомерных действий.
                </p>
            </section>

            <section className='privacy-policy__section'>
                <h2>6. Контактная информация</h2>
                <p>
                    По вопросам, связанным с обработкой персональных данных, вы можете обратиться к Оператору по адресу:
                    Республика Беларусь, г. Минск, ул. В. Хоружей, 3, офис 305а, или по контактным данным, указанным на сайте.
                </p>
            </section>
        </div>
    );
}
